import axios from 'axios';
import difference from 'lodash/difference';
import flatten from 'lodash/flatten';
import { getBasePath, getBundleFiles, getChunkFilePath, getClientBundlesBasePath } from './bundle-helper';
import { getComponentDependencies } from './component-helper';
import { loadFiles, log } from '../../loader-base/src/helpers';
import { camelToDashes, dashesToCamel } from '../../loader-base/src/util';

let loadedFiles;
let validBundles;

/**
 * Check if we should add the specified component file to the download list
 * @param name (string) - name of the component
 * @returns boolean - true if the file should be added to the download list
 */
function shouldAddToDownloadList(name, lazy) {
    if (!lazy.bundlesToDownload || name === 'angular' || lazy.bundlesToDownload[dashesToCamel(name)] || lazy.bundlesToDownload[camelToDashes(name)]) {
        return true;
    }
    return false;
}

/**
 * Check and sort the dependency file list and call the file loader
 * @param {*} componentSource - component source options
 * @param {*} lazy - lazy load options
 * @param {*} resolvedDependencies - resolved dependencies list
 * @param {*} css - css options
 */
function checkAndLoadDependencies(componentSource, lazy, resolvedDependencies, css) {
    return new Promise((resolve) => {
        let dependencyList = [];
        const failedToLoad = [];
        const bundleFileOverrides = componentSource.overrides || {};

        let fileList;

        Object.keys(resolvedDependencies).forEach((name) => {
            if (shouldAddToDownloadList(name, lazy)) {
                const bundleFiles = bundleFileOverrides[dashesToCamel(name)] || bundleFileOverrides[camelToDashes(name)];
                const { version } = resolvedDependencies[name];
                // firstly use override bundles
                // then use the default version bundles
                if (bundleFiles && bundleFiles.length) {
                    fileList = bundleFiles;
                } else if (version) {
                    fileList = getBundleFiles(componentSource.environment, name, version, resolvedDependencies[name].instid);
                } else {
                    fileList = [];
                    failedToLoad.push(name);
                    log.error(`Cannot find the bundle files for component: ${name}`);
                }
                // make sure angular is the first one of the load list
                if (name === 'angular') {
                    dependencyList.unshift(fileList);
                } else {
                    dependencyList.push(fileList);
                }
            }
        });

        dependencyList = flatten(dependencyList);

        // filter the load list with loaded list
        if (!lazy || !lazy.forceRedownload) {
            dependencyList = difference(dependencyList, Array.from(loadedFiles));
        }

        const fileTypeFilter = css.includeComponentCss ? null : ['css'];

        loadFiles(dependencyList, fileTypeFilter).then((loadedList) => {
            Object.keys(loadedList).forEach((file) => {
                loadedFiles.add(file);
            });
            resolve({ resolutions: dependencyList, failedToLoad });
        });
    });
}

/**
 * Get the component dependencies and call to get the files loaded
 * @param {*} loaderConfig - the loader config
 */
function loadComponentDependencies(loaderConfig) {
    return new Promise((resolve) => {
        const resolvedDependencies = getComponentDependencies(loaderConfig);
        loaderConfig.validBundles = loaderConfig.validBundles || {};
        checkAndLoadDependencies(loaderConfig.component.source, loaderConfig.lazy, resolvedDependencies, loaderConfig.css)
            .then(resolutionsAndFailed => resolve({
                resolutions: resolutionsAndFailed.resolutions,
                failedToLoad: resolutionsAndFailed.failedToLoad,
                resolvedDependencies,
            }));
    });
}

/**
 * Get the list of valid bundles in the loader config object, either from the loader config itself or the CDN
 * @param {*} loaderConfig
 */
function getValidBundles(loaderConfig) {
    return new Promise((resolve, reject) => {
        let promise;
        if (loaderConfig.component.source.useClientSpecificBundle) {
            promise = Promise.resolve({
                data: {},
            });
        } else if (validBundles || loaderConfig.validBundles) {
            promise = Promise.resolve({
                data: validBundles || loaderConfig.validBundles,
            });
        } else {
            promise = axios({
                method: 'get',
                url: `${getBasePath(loaderConfig.component.source.environment)}/components.json?_=${new Date().getTime()}`,
            });
        }
        return promise.then((res) => {
            resolve(res.data);
        }).catch(() => {
            reject(loaderConfig);
        });
    });
}

/**
 * Retrieves a list of available bundles (with versions) and downloads the bundles specified in the loader configuration.
 * @param {*} loaderConfig - the loader config
 * @param {set} loadedList - a set with all previously loaded files on the page
 * @returns {Promise} - Resolves with a manifest containing the loader configuration, app config and a list of the loaded files.
 */
function bundlesReady(loaderConfig, loadedList) {
    return new Promise((resolve, reject) => {
        loadedFiles = loadedList || new Set();
        getValidBundles(loaderConfig).then((res) => {
            validBundles = res;
            loaderConfig.validBundles = validBundles;
            loadComponentDependencies(loaderConfig).then(resolutionsAndResolvedDeps => resolve({
                resolutions: resolutionsAndResolvedDeps.resolutions,
                resolvedDependencies: resolutionsAndResolvedDeps.resolvedDependencies,
                loaderConfig,
                config: loaderConfig.config,
                failedToLoad: resolutionsAndResolvedDeps.failedToLoad,
            }));
        }).catch((e) => {
            reject(e);
        });
    });
}

/**
 * Update the loaderConfig object if useClientSpecificBundle is set to true
 * @param {*} loaderConfig - the loader config
 */
function handleClientSpecificBundle(loaderConfig) {
    return new Promise((resolve, reject) => {
        if (loaderConfig.component.source && loaderConfig.component.source.useClientSpecificBundle) {
            const { namespace } = loaderConfig.configuration;
            if (!namespace) {
                reject(new Error('Missing configuration.namespace (mandatory when using "useClientSpecificBundle")'));
                return;
            }

            const basePath = getClientBundlesBasePath(loaderConfig.component.source.environment);
            const firstComponentInstance = Object.keys(loaderConfig.config.components)[0];
            const firstComponentType = loaderConfig.config.components[firstComponentInstance].type;
            const namespacePath = namespace.replace(/\./g, '/');
            let version = loaderConfig.component.source.clientSpecificBundleVersion;
            let promiseChain = Promise.resolve();

            if (!version) {
                promiseChain = promiseChain.then(() => axios({
                    method: 'get',
                    url: `${basePath}/${namespacePath}/${namespace}-bundle.json`,
                })).then((res) => {
                    version = res.data ? res.data.revision : undefined;
                }).catch(() => reject(new Error('Failed to retrieve metadata file from CDN')));
            }
            promiseChain.then(() => {
                if (version) {
                    loaderConfig.component.source.overrides = {
                        [firstComponentType]: [
                            `${basePath}/${namespacePath}/${version}/${namespace}-bundle.js`,
                            `${basePath}/${namespacePath}/${version}/${namespace}-bundle.css`,
                        ],
                    };
                    resolve();
                } else {
                    reject(new Error('Metadata file did not contain version/revision number'));
                }
            });
        } else {
            resolve();
        }
    });
}

/**
 * Reset the validBundles variable which stores
 * the result of the call to components.json
 */
function resetValidBundles() {
    validBundles = undefined;
}

/**
 * Initialise the object to store the webpack public path
 */
function initWebpackPublicPath() {
    window.morningstar = window.morningstar || {};
    window.morningstar.loader = window.morningstar.loader || {};
    window.morningstar.loader.webpackPublicPath = window.morningstar.loader.webpackPublicPath || {};
}

/**
 * Update the webpack public path for a component
 */
function setComponentPublicPath(componentName, path) {
    window.morningstar.loader.webpackPublicPath[componentName] = path;
}

/**
 * Set the chunks path to be used by webpack
 * This sets the __webpack_public_path__ free variable in a bundle so that chunks are downloaded from the correct URL
 */
function setBundleChunksPath(loaderConfig) {
    return new Promise((resolve) => {
        const { chunks, environment } = loaderConfig.component.source;
        initWebpackPublicPath();

        if (chunks === true) {
            // Find out which dependencies we are using (DEFAULT or instid)
            const deps = getComponentDependencies(loaderConfig);

            // Assume that all bundle files need the chunks path set
            Object.keys(loaderConfig.config.components).forEach((instance) => {
                if (loaderConfig.config.components[instance].type) {
                    const componentName = camelToDashes(loaderConfig.config.components[instance].type);
                    const dependency = deps[componentName] || {};
                    if (dependency.version && dependency.instid) {
                        setComponentPublicPath(componentName, getChunkFilePath(environment, componentName, dependency.version, dependency.instid));
                    }
                }
            });
        } else if (chunks) {
            Object.keys(chunks).forEach((componentName) => {
                setComponentPublicPath(componentName, chunks[componentName]);
            });
        }
        resolve();
    });
}

export {
    bundlesReady,
    getValidBundles,
    handleClientSpecificBundle,
    resetValidBundles,
    setBundleChunksPath,
};



// WEBPACK FOOTER //
// ./src/bundle-loader.js